import { DFPModal } from "@/components/modals/dfp-modal.js";
import {
	type ChecklistBrandOption,
	downloadChecklistsZipResult,
	getChecklistsZipBrands,
	getChecklistsZipJobStatus,
	startChecklistsZipJob,
} from "@/services/organization-service";
import {
	Alert,
	DatePicker,
	Form,
	Progress,
	Radio,
	Select,
	Typography,
	message,
} from "antd";
import type { Dayjs } from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";

const { Text } = Typography;

interface ChecklistZipExportModalProps {
	open: boolean;
	onClose: () => void;
	/** Location currently being viewed (if scoped to a single location). */
	browserLocationId?: string;
	/** Default checklist form id (e.g. the form the table is currently showing). */
	defaultFormId?: number;
}

const checklistFormOptions = [
	{ label: "Handyman Checklist", value: 2 },
	{ label: "QC Checklist", value: 4 },
	{ label: "Pre-Walk Checklist", value: 6 },
];

const POLL_INTERVAL_MS = 2000;

/**
 * Vixxo-only bulk checklist export. Starts an async job, shows progress, then
 * downloads a ZIP containing one PDF per checklist submission, organized into a
 * folder per location.
 */
export function ChecklistZipExportModal({
	open,
	onClose,
	browserLocationId,
	defaultFormId,
}: ChecklistZipExportModalProps) {
	const hasLocation = Boolean(browserLocationId);
	const [running, setRunning] = useState(false);
	const [progress, setProgress] = useState<{
		completed: number;
		total: number;
	} | null>(null);

	const [formId, setFormId] = useState<number>(
		defaultFormId && [2, 4, 6].includes(defaultFormId) ? defaultFormId : 2,
	);
	const [scope, setScope] = useState<"location" | "all">(
		hasLocation ? "location" : "all",
	);
	const [brand, setBrand] = useState<string>("");
	const [brandOptions, setBrandOptions] = useState<ChecklistBrandOption[]>([]);
	const [brandsLoading, setBrandsLoading] = useState(false);
	const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

	// A brand must be chosen when exporting across all locations.
	const brandRequired = scope === "all";

	// Load the brand options whenever the modal opens or the checklist type
	// changes (brands are scoped to the selected form).
	useEffect(() => {
		if (!open) return;
		let active = true;
		setBrandsLoading(true);
		getChecklistsZipBrands(formId)
			.then((options) => {
				if (!active) return;
				setBrandOptions(options);
				// Drop any previously selected brand that no longer applies.
				setBrand((current) =>
					options.some((o) => o.brand === current) ? current : "",
				);
			})
			.catch((err) => {
				if (!active) return;
				message.error(
					err instanceof Error ? err.message : "Failed to load brands",
				);
				setBrandOptions([]);
			})
			.finally(() => {
				if (active) setBrandsLoading(false);
			});
		return () => {
			active = false;
		};
	}, [open, formId]);

	// Guards so an in-flight poll loop stops if the modal closes/unmounts.
	const cancelledRef = useRef(false);
	useEffect(() => {
		return () => {
			cancelledRef.current = true;
		};
	}, []);

	const handleClose = useCallback(() => {
		if (running) return;
		onClose();
	}, [running, onClose]);

	const handleExport = useCallback(async () => {
		if (brandRequired && !brand) {
			message.warning("Please select a brand to export.");
			return;
		}
		cancelledRef.current = false;
		setRunning(true);
		setProgress(null);
		try {
			const [start, end] = range || [null, null];
			const { jobId, total, filename } = await startChecklistsZipJob({
				formId,
				locationId:
					scope === "location" && browserLocationId
						? browserLocationId
						: undefined,
				brand: scope === "all" && brand ? brand : undefined,
				startDate: start ? start.format("YYYY-MM-DD") : undefined,
				// Make the end date inclusive of the whole selected day.
				endDate: end ? end.add(1, "day").format("YYYY-MM-DD") : undefined,
			});

			setProgress({ completed: 0, total });

			// Poll until the job finishes.
			let done = false;
			while (!done) {
				await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
				if (cancelledRef.current) return;

				const status = await getChecklistsZipJobStatus(jobId);
				setProgress({ completed: status.completed, total: status.total });

				if (status.status === "done") {
					done = true;
				} else if (status.status === "error") {
					throw new Error(status.error || "Export failed while processing");
				}
			}

			if (cancelledRef.current) return;
			await downloadChecklistsZipResult(jobId, filename);
			message.success("Checklist ZIP downloaded.");
			setRunning(false);
			onClose();
		} catch (err) {
			if (cancelledRef.current) return;
			message.error(
				err instanceof Error ? err.message : "Failed to export checklists",
			);
			setRunning(false);
		}
	}, [formId, scope, brand, brandRequired, range, browserLocationId, onClose]);

	const percent =
		progress && progress.total > 0
			? Math.floor((progress.completed / progress.total) * 100)
			: 0;

	return (
		<DFPModal
			open={open}
			className="checklist-zip-export-modal"
			onCancel={handleClose}
			onOk={handleExport}
			title="Download Checklists (PDF ZIP)"
			confirmLoading={running}
			okText={running ? "Generating..." : "Download ZIP"}
			okButtonProps={{ disabled: brandRequired && !brand }}
			cancelButtonProps={{ disabled: running }}
		>
			<Form layout="vertical">
				<Alert
					type="info"
					showIcon
					className="mb-3"
					message="One PDF per checklist, organized into a folder per location."
					description="Large exports run in the background and can take a few minutes. Keep this window open until the download starts."
				/>

				<Form.Item label="Checklist Type" required>
					<Select
						options={checklistFormOptions}
						value={formId}
						onChange={(value) => setFormId(value)}
						disabled={running}
					/>
				</Form.Item>

				{hasLocation && (
					<Form.Item label="Scope">
						<Radio.Group
							value={scope}
							onChange={(e) => setScope(e.target.value)}
							disabled={running}
						>
							<Radio value="location">This location only</Radio>
							<Radio value="all">All locations</Radio>
						</Radio.Group>
					</Form.Item>
				)}

				{scope === "all" && (
					<Form.Item
						label="Brand"
						required
						help="Choose which brand to export. This keeps exports to a manageable size."
					>
						<Select
							placeholder={
								brandsLoading ? "Loading brands..." : "Select a brand"
							}
							value={brand || undefined}
							onChange={(value) => setBrand(value)}
							loading={brandsLoading}
							disabled={running || brandsLoading}
							showSearch
							optionFilterProp="label"
							notFoundContent={
								brandsLoading ? "Loading..." : "No brands with checklists"
							}
							options={brandOptions.map((o) => ({
								label: `${o.brand} (${o.count})`,
								value: o.brand,
							}))}
						/>
					</Form.Item>
				)}

				<Form.Item label="Date Range (optional)">
					<DatePicker.RangePicker
						format="MM/DD/YYYY"
						className="w-100"
						value={range as never}
						onChange={(dates) =>
							setRange(dates as [Dayjs | null, Dayjs | null] | null)
						}
						disabled={running}
					/>
				</Form.Item>

				{progress && (
					<div className="mt-3">
						<Progress percent={percent} status="active" />
						<Text type="secondary">
							Generated {progress.completed} of {progress.total} checklists...
						</Text>
					</div>
				)}
			</Form>
		</DFPModal>
	);
}
